import { SPLIT_PROMPT_CHECKOUT_SOURCE } from "./party-splits";

export const PARTY_PASS_CHECKOUT_STARTED_EVENT = "party_pass_checkout_started";
export const PARTY_PASS_UNLOCKED_EVENT = "party_pass_unlocked";
export const PARTY_PASS_CHECKOUT_ABANDONED_EVENT =
  "party_pass_checkout_abandoned";
export const PARTY_PASS_CHECKOUT_SOURCE_METADATA_KEY = "checkoutSource";

export const PARTY_PASS_CHECKOUT_SOURCES = [
  "party_home_live_reactions",
  "party_cap_banner",
  "party_settings",
  "party_chat",
  "party_invite",
  "party_recap",
  "party_recap_wrapped",
  "split_prompt",
  "mobile_api",
  "jev_cap",
  "jev_chat",
  "jev_live_reactions",
  "jev_leaderboard",
  "jev_recap",
  "jev_blocked_join",
] as const;

export type PartyPassCheckoutSource = (typeof PARTY_PASS_CHECKOUT_SOURCES)[number];

const CHECKOUT_SOURCE_SET = new Set<string>(PARTY_PASS_CHECKOUT_SOURCES);

export function isPartyPassCheckoutSource(
  value: unknown,
): value is PartyPassCheckoutSource {
  return typeof value === "string" && CHECKOUT_SOURCE_SET.has(value);
}

export function parsePartyPassCheckoutSource(
  value: unknown,
): PartyPassCheckoutSource | null {
  return isPartyPassCheckoutSource(value) ? value : null;
}

export function resolvePartyPassCheckoutSource(
  value: unknown,
  fallback: PartyPassCheckoutSource,
): PartyPassCheckoutSource {
  return parsePartyPassCheckoutSource(value) ?? fallback;
}

export function buildPartyPassCheckoutSessionMetadata(input: {
  partyId: string;
  userId: string;
  billingProductKey: string;
  source?: string | null;
  clerkUserId?: string | null;
}): Record<string, string> {
  const source = parsePartyPassCheckoutSource(input.source);
  const clerkUserId = input.clerkUserId?.trim() ?? "";
  return {
    partyId: input.partyId,
    userId: input.userId,
    plan: "party_pass",
    billingProductKey: input.billingProductKey,
    ...(clerkUserId ? { clerkUserId } : {}),
    ...(source
      ? { [PARTY_PASS_CHECKOUT_SOURCE_METADATA_KEY]: source }
      : {}),
    ...(source === SPLIT_PROMPT_CHECKOUT_SOURCE
      ? { unlockSource: SPLIT_PROMPT_CHECKOUT_SOURCE }
      : {}),
  };
}

export function getPartyPassCheckoutSourceFromMetadata(
  metadata: Record<string, string> | null | undefined,
): PartyPassCheckoutSource | null {
  return (
    parsePartyPassCheckoutSource(
      metadata?.[PARTY_PASS_CHECKOUT_SOURCE_METADATA_KEY],
    ) ?? parsePartyPassCheckoutSource(metadata?.unlockSource)
  );
}

export const PARTY_PASS_UPSELL_REASONS = [
  "cap",
  "chat",
  "leaderboard",
  "live_reactions",
  "recap",
  "blocked_join",
] as const;

export type PartyPassUpsellReason = (typeof PARTY_PASS_UPSELL_REASONS)[number];

const CHECKOUT_SOURCE_TO_UPSELL_REASON: Record<
  PartyPassCheckoutSource,
  PartyPassUpsellReason
> = {
  party_home_live_reactions: "live_reactions",
  party_cap_banner: "cap",
  party_settings: "cap",
  party_chat: "chat",
  party_invite: "cap",
  party_recap: "recap",
  party_recap_wrapped: "recap",
  split_prompt: "cap",
  mobile_api: "cap",
  jev_cap: "cap",
  jev_chat: "chat",
  jev_live_reactions: "live_reactions",
  jev_leaderboard: "leaderboard",
  jev_recap: "recap",
  jev_blocked_join: "blocked_join",
};

export function partyPassUpsellReasonFromCheckoutSource(
  source: PartyPassCheckoutSource | null | undefined,
): PartyPassUpsellReason {
  if (!source) {
    return "cap";
  }
  return CHECKOUT_SOURCE_TO_UPSELL_REASON[source];
}

export function partyPassUpsellSurfaceFromCheckoutSource(
  source: PartyPassCheckoutSource | null | undefined,
): string {
  return source ?? "unknown";
}

export function isPartyPassUpgradeAtOrOverCap(
  memberCount: number | null | undefined,
  memberCap: number | null | undefined,
): boolean | null {
  if (
    memberCap == null ||
    memberCount == null ||
    !Number.isFinite(memberCount) ||
    !Number.isFinite(memberCap)
  ) {
    return null;
  }

  return memberCount >= memberCap;
}

export function buildPartyPassUnlockedEventProperties(input: {
  partyId: string;
  awardShowId?: string | null;
  source: PartyPassCheckoutSource | null;
  unlockSource: "stripe" | "split_prompt" | "apple_iap" | "google_play";
  memberCount: number | null;
  memberCap: number | null;
  amountCents: number | null;
  currency: string | null;
  hoursToPaid?: number | null;
  hadBlockedJoins?: boolean | null;
}): {
  party_id: string;
  award_show_id: string | null;
  plan: "party_pass";
  source: PartyPassCheckoutSource | null;
  surface: PartyPassCheckoutSource | null;
  reason: PartyPassUpsellReason | null;
  unlock_source: "stripe" | "split_prompt" | "apple_iap" | "google_play";
  member_count: number | null;
  member_cap: number | null;
  prior_participant_count: number | null;
  participant_count: number | null;
  at_or_over_cap: boolean | null;
  amount_cents: number | null;
  currency: string | null;
  hours_to_paid: number | null;
  had_blocked_joins: boolean | null;
} {
  return {
    party_id: input.partyId,
    award_show_id: input.awardShowId ?? null,
    plan: "party_pass",
    source: input.source,
    surface: input.source,
    reason: input.source
      ? partyPassUpsellReasonFromCheckoutSource(input.source)
      : null,
    unlock_source: input.unlockSource,
    member_count: input.memberCount,
    member_cap: input.memberCap,
    prior_participant_count: input.memberCount,
    participant_count: input.memberCount,
    at_or_over_cap: isPartyPassUpgradeAtOrOverCap(
      input.memberCount,
      input.memberCap,
    ),
    amount_cents: input.amountCents,
    currency: input.currency,
    hours_to_paid: input.hoursToPaid ?? null,
    had_blocked_joins: input.hadBlockedJoins ?? null,
  };
}

export function partyPassCheckoutAbandonedStorageKey(partyId: string): string {
  return `party-pass-checkout-abandoned:${partyId.trim()}`;
}

export function withCheckoutCancelSignal(path: string, partyId: string): string {
  const hashIndex = path.indexOf("#");
  const withoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : "";
  const queryIndex = withoutHash.indexOf("?");
  const pathname =
    queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const query = queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : "";
  const params = new URLSearchParams(query);
  if (params.get("upgrade") !== "success") {
    params.set("upgrade", "canceled");
  }
  const segment = pathname.match(/^\/parties\/([^/]+)/)?.[1];
  const checkoutPartyId = partyId.trim();
  if ((!segment || segment === "create") && checkoutPartyId) {
    params.set("checkoutParty", checkoutPartyId);
  }
  const next = params.toString();
  return `${pathname}${next ? `?${next}` : ""}${hash}`;
}

export function partyIdFromCheckoutCancelLocation(input: {
  pathname: string;
  upgrade?: string | null;
  checkoutParty?: string | null;
}): string | null {
  if (input.upgrade !== "canceled") {
    return null;
  }
  const segment = input.pathname.match(/^\/parties\/([^/]+)/)?.[1];
  if (segment && segment !== "create") {
    return segment;
  }
  const checkoutParty = input.checkoutParty?.trim() ?? "";
  return checkoutParty || null;
}
